import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "src/lib/utils";

const linkVariants = cva(
  "cursor-pointer text-primary transition-colors duration-150 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
  {
    variants: {
      variant: {
        default: "",
        h1: "text-[2.25rem] font-normal leading-tight",
        h2: "text-[1.75rem] font-semibold leading-[1.875rem]",
        h3: "text-[1.375rem] font-medium leading-normal",
        h4: "text-[1.25rem] font-semibold leading-[1.875rem]",
        h5: "text-[1.125rem] font-semibold leading-[1.875rem]",
        h6: "text-base font-semibold leading-[1.875rem]",
        subtitle1: "text-base font-medium leading-7",
        subtitle2: "text-sm font-medium leading-6",
        body1: "text-base font-normal leading-6",
        body2: "text-sm font-normal leading-5",
        caption: "text-xs font-normal leading-[1.125rem]",
        overline:
          "text-xs font-normal leading-[1.875rem] uppercase tracking-wider",
        inherit: "",
      },
      underline: {
        always: "underline",
        hover: "no-underline hover:underline",
        none: "no-underline hover:no-underline",
      },
    },
    defaultVariants: {
      variant: "body2",
      underline: "hover",
    },
  }
);

export interface LinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof linkVariants> {
  asChild?: boolean;
  component?: React.ElementType;
  to?: string;
}

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      className,
      variant,
      underline,
      asChild = false,
      style,
      component: Component,
      to,
      children,
      ...props
    },
    ref
  ) => {
    // If component prop is provided (e.g., RouterLink), render with that
    if (Component) {
      return (
        <Component
          className={cn(linkVariants({ variant, underline, className }))}
          style={style}
          to={to}
          ref={ref}
          {...props}
        >
          {children}
        </Component>
      );
    }

    // If asChild, use Slot to merge props onto child
    if (asChild) {
      return (
        <Slot
          className={cn(linkVariants({ variant, underline, className }))}
          style={style}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    // Default: render as anchor
    return (
      <a
        className={cn(linkVariants({ variant, underline, className }))}
        style={style}
        ref={ref}
        {...props}
      >
        {children}
      </a>
    );
  }
);

Link.displayName = "Link";

export { Link, linkVariants };
